import { JanusJS } from 'lib/janus';
import { formatErrorForLogging } from 'utils/logger';
import { RTCClient } from 'utils/webrtc';
import {
  ControlledReceivingHandle,
  VideoroomConnectionState,
} from 'utils/webrtc/ControlledReceivingHandle';
import { sendMessage, startSubscriptionTemplate } from 'utils/webrtc/messages';
import {
  SubscribeAnswerAttributes,
  SubscribeAnswerOutcome,
  emitSubscribeAnswer,
} from 'utils/webrtc/subscribeTelemetry';

export const ANSWER_RESEND_DELAY_MS = 300;
const MAX_ANSWER_RESENDS = 2;

const pause = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

// Janus learns our ICE credentials only from this answer, so a lost one leaves the handle silent for
// good. Gated on handle state, not on the error: deliberate teardowns fail with the same text.
export const deliverSubscriberAnswer = (
  feed: ControlledReceivingHandle,
  jsep: JanusJS.JSEP,
  receiver: SubscribeAnswerAttributes['receiver'],
  isCurrentHandle: () => boolean
): Promise<void> => {
  const isLive = () =>
    !RTCClient.supressErrors &&
    isCurrentHandle() &&
    feed.connectionState !== VideoroomConnectionState.disconnecting;

  const report = (outcome: SubscribeAnswerOutcome, resends: number, error: string) =>
    emitSubscribeAnswer({ outcome, receiver, handle: feed.handle, resends, error });

  const attempt = async (resends: number, lastError: string): Promise<void> => {
    try {
      await sendMessage(feed.janusPlugin, startSubscriptionTemplate(RTCClient.roomId!), jsep);
    } catch (error) {
      if (!isLive()) {
        return;
      }

      const { message } = formatErrorForLogging(error);

      if (resends === MAX_ANSWER_RESENDS) {
        report('exhausted', resends, message);
        return;
      }

      await pause(ANSWER_RESEND_DELAY_MS);

      if (isLive()) {
        await attempt(resends + 1, message);
      }

      return;
    }

    if (resends > 0) {
      report('retried_ok', resends, lastError);
    }
  };

  return attempt(0, '');
};
